import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DashboardLayout from '../shared/layout/DashboardLayout';
import { FaEye } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import { getAdminMenuItems } from '../../utils/menuItems';
import '../../styles/App.css';

const PublikasiPage = () => {
  const tableRef = useRef(null);
  const [dataList, setDataList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const decoded = jwtDecode(token);

      if (!token || !decoded.twoFactorVerified) {
        navigate('/');
        return;
      }

      if (decoded.role !== 0 && decoded.role !== 2) {
        navigate('/');
        return;
      }

      const response = await axios.get(`${config.API_URL}/dataAdmin`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const publishedData = response.data.filter(item => item.status_publikasi === 'Sudah Publikasi');
      const sortedData = publishedData.sort((a, b) =>
        new Date(b.last_modified || 0) - new Date(a.last_modified || 0)
      );

      setDataList(sortedData);
      setFilteredData(sortedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Tidak Ada Data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setSearchLoading(true);
    setTimeout(() => {
      let filtered = [...dataList];

      if (searchText) {
        const query = searchText.toLowerCase();
        filtered = filtered.filter((row) =>
          row.no_tiket.toLowerCase().includes(query) ||
          row.nama_igt.toLowerCase().includes(query) ||
          row.kl.toLowerCase().includes(query)
        );
      }

      setFilteredData(filtered);
      setSearchLoading(false);
    }, 500);
  };

  const columns = [
    {
      name: 'No Tiket',
      selector: row => row.no_tiket,
      sortable: true,
      minWidth: '150px',
    },
    {
      name: 'Nama IGT',
      selector: row => row.nama_igt,
      sortable: true,
      minWidth: '250px',
      cell: row => (
        <div data-tooltip-id="nama-tooltip" data-tooltip-content={row.nama_igt}>
          {row.nama_igt.length > 100 ? `${row.nama_igt.substring(0, 100)}...` : row.nama_igt}
        </div>
      )
    },
    {
      name: 'KL',
      selector: row => row.kl,
      sortable: true,
      minWidth: '150px',
      cell: row => (
        <div data-tooltip-id="kl-tooltip" data-tooltip-content={row.kl}>
          {row.kl.length > 50 ? `${row.kl.substring(0, 50)}...` : row.kl}
        </div>
      )
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const statusMap = {
          'Sudah Terintegrasi': 'status-sudah-terintegrasi',
          'Sedang Verifikasi': 'status-sedang-verifikasi',
          'Revisi Pengajuan': 'status-revisi-pengajuan',
          'Perlu Perbaikan': 'status-perlu-perbaikan',
          'Belum Verifikasi': 'status-belum-diverifikasi'
        };
        const statusClass = statusMap[row.status] || 'status-default';

        return (
          <span className={`badge ${statusClass}`}>
            {row.status}
          </span>
        );
      }
    },
    {
      name: 'Status Publikasi',
      selector: row => row.status_publikasi || 'Belum Publikasi',
      sortable: true,
      minWidth: '150px',
      cell: row => (
        <span className="badge status-sudah-terintegrasi">
          {row.status_publikasi || 'Belum Publikasi'}
        </span>
      )
    },
    {
      name: 'Terakhir Diupdate',
      selector: row => row.last_modified ? new Date(row.last_modified) : new Date(0),
      sortable: true,
      minWidth: '150px',
      cell: row => {
        const displayDate = row.last_modified
          ? new Date(row.last_modified).toLocaleDateString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
          : '-';
        return <div>{displayDate}</div>;
      }
    },
    {
      name: 'Aksi',
      cell: row => (
        <Link
          to={`/rekapdata/${btoa(row.no_tiket)}`}
          className="action-button view-button"
        >
          <FaEye /> Lihat
        </Link>
      ),
      minWidth: '100px',
    }
  ];

  const conditionalRowStyles = [
    {
      when: row => {
        return (row.is_published === false || !row.is_published) && !row.is_igt_summary && !row.is_detail_row;
      },
      style: {
        backgroundColor: '#fef3c7',
        borderLeft: '4px solid #fbbf24',
      },
    },
  ];

  const menuItems = getAdminMenuItems();

  return (
    <DashboardLayout menuItems={menuItems} userRole="Admin">
      <div className="container mt-4">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/admin">Dashboard</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Data Terpublikasi
            </li>
          </ol>
        </nav>

        <div className="card shadow-lg p-4 mb-4">
          <div className="search-container">
            <input
              type="text"
              className="filter-input"
              placeholder="Cari No Tiket, Nama IGT, atau KL"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              className="btn btn-cari search-button"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              <i className="fas fa-search"></i> Cari
            </button>
          </div>
        </div>

        <div className="card shadow-lg p-4 printable" ref={tableRef}>
          <h3 className="title">
            Data Terpublikasi
            <span className="badge bg-success ms-2">{filteredData.length} Data</span>
          </h3>

          {error && <div className="alert-danger">{error}</div>}

          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={loading}
            progressComponent={<LoadingSpinner message="Memuat data..." type="spinner" size="medium" />}
            pagination
            highlightOnHover
            pointerOnHover
            defaultSortFieldId={1}
            defaultSortAsc={false}
            noDataComponent={<p className="text-center text-muted">Tidak ada data yang tersedia.</p>}
            responsive
            conditionalRowStyles={conditionalRowStyles}
          />
        </div>

        <Tooltip id="nama-tooltip" />
        <Tooltip id="kl-tooltip" />

        {searchLoading && (
          <div className="loading-overlay-fullscreen">
            <LoadingSpinner message="Mencari data..." type="dots" size="large" />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PublikasiPage;